import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CardCount } from "../../components/game/CardCount.js";
import { CoinPurse } from "../../components/game/CoinPurse.js";
import { SideBoard } from "../../components/game/SideBoard.js";
import type { GameRules, Snapshot } from "../../services/generated/client.js";
import type { GameView } from "../../services/gameLink.js";
import { cn } from "../../lib/utils.js";
import { sideLabel } from "../../lib/roster.js";
import {
  challengedSlotsFor,
  claimedSlotFor,
  guessTextFor,
  wonByChallenge,
} from "../../lib/placement.js";

/**
 * Everybody's timelines, arranged for the room the game is being played in.
 *
 * # The layout is the whole difference between couch and remote
 *
 * In **couch** mode there is a television, and it is the board. What the phone
 * needs is a glance — *am I close, is anybody closer* — so the other sides stay
 * a compact strip and the controls own the screen. Your own timeline is not
 * here at all: it sits inline with the placement control, where the decision is
 * being made.
 *
 * In **remote** mode there is no television. This *is* the board, it is the only
 * board there is, and it goes first — every side including your own, at the
 * width the device actually has rather than the phone-shaped column the
 * controls want. A board below the fold under a shop is one nobody plays off.
 *
 * That is the entire transport-level content of remote mode, which is ADR 0005
 * working: "who renders the board" was made a property of a *layout* in phase 6,
 * so phase 7 is an arrangement rather than a second surface.
 *
 * # ⚠️ At four players on a phone, the board *is* the fold — finding #10
 *
 * `sm:grid-cols-2` only applies from 640px, so on a phone every side stacked in
 * **one column**, above the controls and the shop, because remote mode puts the
 * board first. The reasoning for that is still right and is quoted above; what
 * nobody anticipated is that four stacked panels are the whole screen, and
 * everything else — including the clock (#3) — is below them.
 *
 * So: the **active side is expanded for everybody** and the rest collapse to one
 * line — name, cards, coins — which you tap to open. The active side is exactly
 * what matters at that moment: where the card is being placed, what challenges
 * are aimed at, what the round is about. The board stays first *and* stops being
 * the whole screen.
 *
 * Two questions the finding left open, answered here:
 *
 * - **Your own side stays collapsed unless you open it.** In couch mode your
 *   timeline is deliberately not in this list at all; making remote mode
 *   special-case it would be a third arrangement to keep in step.
 * - **An expanded side stays open across rounds.** *Stays* is kinder than
 *   *resets*, and a panel that closed itself under somebody's thumb is the kind
 *   of unbidden UI finding #19 is about.
 */
export function Sides({
  board,
  rules,
  roster,
  yourSide,
  layout,
  omit = [],
  className,
}: {
  board: Snapshot;
  rules: GameRules;
  roster: GameView["roster"];
  /** Which side is the viewer's, so it can be marked. `null` for a spectator. */
  yourSide: string | null;
  /** `couch` · `remote`. */
  layout: string;
  className?: string;
  /**
   * Sides already drawn somewhere else on this screen.
   *
   * 🔴 **Finding 3.10**: opening the drawer rendered the active side's board a
   * second time, immediately under the copy the stage was already showing —
   * *"a repeated card is rendered and in the middle you have your card but it is
   * overlapping with no margin."*
   *
   * The rule to avoid it was already here and only half-applied: couch layout
   * filtered out **your own** side because the placement control draws it
   * inline. But `RoundStage` draws the **active** side, whoever that is, and
   * nothing filtered that — so every round somebody else played showed their
   * board twice, and `remote` (which filters nothing) showed it twice always.
   *
   * Naming it as a prop rather than assuming `active_side` keeps the component
   * honest: it does not get to guess what its parent painted.
   */
  omit?: readonly string[];
}) {
  const { t } = useTranslation();
  // Which collapsed sides the viewer has opened. Never reset on its own — see
  // the note above.
  const [opened, setOpened] = useState<readonly string[]>([]);
  const remote = layout === "remote";
  const shown = (
    remote
      ? board.sides
      : // Couch: your own timeline is rendered inline with the placement
        // control, so repeating it here would put the same cards on screen
        // twice.
        board.sides.filter((side) => side.id !== yourSide)
  ).filter((side) => !omit.includes(side.id));

  if (shown.length === 0) {
    return null;
  }

  return (
    <section
      aria-label={remote ? t("sides.board") : t("sides.others")}
      className={cn(
        "grid min-w-0 gap-3",
        remote && shown.length > 1 && "sm:grid-cols-2",
        className,
      )}
    >
      {shown.map((side) => {
        const isActive = side.id === board.active_side;
        // Collapsed only where space is actually short. From `sm` up the grid
        // has two columns and there is nothing to save.
        // ⚠️ **Collapsed by CSS, not by branching.** Both are always in the
        // document and the breakpoint picks: from `sm` up the grid has two
        // columns and there is nothing to save, so a side that only rendered its
        // one-line summary would simply be missing on a laptop.
        const collapsed = !isActive && !opened.includes(side.id);
        return (
          <div key={side.id} className="min-w-0">
            {collapsed && (
              <button
                type="button"
                data-testid="side-collapsed"
                aria-expanded={false}
                onClick={() => {
                  setOpened((open) => [...open, side.id]);
                }}
                className="flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl border-2 border-white/10 px-3 text-left sm:hidden"
              >
                <span className="min-w-0 truncate text-sm font-semibold">
                  {sideLabel(roster, side.id)?.name ?? t("side.left")}
                  {side.id === yourSide && (
                    <span className="text-flash-400"> {t("side.yours")}</span>
                  )}
                </span>
                {/* ⚠️ **The coins keep their coin.** This row read *"3 cards ·
                    4"*, and the 4 was a bare digit whose meaning you had to
                    remember — on the one surface that exists to be read at a
                    glance. Same component as everywhere else, half the size. */}
                <span className="flex shrink-0 items-center gap-3">
                  <CardCount size="tight" count={side.card_count} />
                  <CoinPurse size="tight" coins={side.coins} />
                </span>
              </button>
            )}
            <SideBoard
              className={collapsed ? "hidden sm:block" : ""}
              side={side}
              label={sideLabel(roster, side.id)}
              active={isActive}
              yours={side.id === yourSide}
              cardsToWin={rules.cards_to_win}
              claimedSlot={claimedSlotFor(board, side.id)}
              // **Phase 18, finding #9.** The claims against you, on your own
              // board. These were drawn only inside a challenger's panel, so the
              // person whose card was at stake was the one player who could not
              // see them.
              takenSlots={challengedSlotsFor(board, side.id, roster)}
              guess={guessTextFor(board, side.id)}
              wonByChallenge={wonByChallenge(board, side.id)}
              outcome={outcomeFor(board, side.id)}
            />
          </div>
        );
      })}
    </section>
  );
}

/**
 * How a side did this round, once that is public.
 *
 * `board.round.outcomes` is empty until `Reveal` — by construction, server-side
 * — so this is `null` for most of a round without needing to ask what phase it
 * is. That is the point: a phase check here would be a second place the reveal
 * rule lives, and the one that drifts.
 */
function outcomeFor(board: Snapshot, side: string) {
  return board.round?.outcomes.find((outcome) => outcome.side === side) ?? null;
}
